import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { generateAPI, authAPI } from '../services/api';

const Generate = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('text');
  const [textPrompt, setTextPrompt] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [generatedImage, setGeneratedImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const generateText = async () => {
    if (!textPrompt.trim()) {
      setError('Por favor ingresa un prompt para generar texto');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await generateAPI.generateText({
        prompt: textPrompt,
        max_tokens: 500,
        model: 'gpt-4o-mini'
      });

      if (response.data.success) {
        setGeneratedText(response.data.text);
        setSuccess('¡Texto generado exitosamente!');
      } else {
        setError(response.data.error || 'Error al generar texto');
      }
    } catch (err) {
      console.error('Error generating text:', err);
      setError(err.response?.data?.detail || 'Error al conectar con el servicio');
    } finally {
      setLoading(false);
    }
  };

  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      setError('Por favor ingresa un prompt para generar imagen');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await generateAPI.generateImage({
        prompt: imagePrompt,
        size: '1024x1024',
        quality: 'standard'
      });

      if (response.data.success) {
        // Handle both url and image_base64 formats
        const imageUrl = response.data.url || response.data.image_url || 
          (response.data.image_base64 ? `data:image/png;base64,${response.data.image_base64}` : null);
        setGeneratedImage(imageUrl);
        setSuccess('¡Imagen generada exitosamente!');
      } else {
        setError(response.data.error || 'Error al generar imagen');
      }
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err.response?.data?.detail || 'Error al conectar con el servicio');
    } finally {
      setLoading(false);
    }
  };

  const copyText = () => {
    navigator.clipboard.writeText(generatedText);
    setSuccess('Texto copiado al portapapeles');
    setTimeout(() => setSuccess(''), 2000);
  };

  const downloadImage = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = 'gpt-jdp-generated-image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="px-4 sm:px-10 md:px-20 lg:px-40">
        <Header />
        
        <div className="py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              🚀 Centro de Generación IA
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              Crea contenido increíble con los modelos de IA más avanzados
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('text')}
                className={`pb-3 px-4 font-medium transition-colors ${
                  activeTab === 'text'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 dark:text-gray-400 hover:text-primary'
                }`}
              >
                📝 Generación de Texto
              </button>
              <button
                onClick={() => setActiveTab('image')}
                className={`pb-3 px-4 font-medium transition-colors ${
                  activeTab === 'image'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 dark:text-gray-400 hover:text-primary'
                }`}
              >
                🎨 Generación de Imágenes
              </button>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-400 rounded-lg">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {activeTab === 'text' ? '📝 Crear Contenido' : '🎨 Crear Imagen'}
              </h2>

              {activeTab === 'text' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Describe qué texto quieres generar:
                  </label>
                  <textarea
                    value={textPrompt}
                    onChange={(e) => setTextPrompt(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-[#283039] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                    rows="6"
                    placeholder="Ej: Escribe un artículo sobre inteligencia artificial en 300 palabras..."
                  />
                  <div className="mt-4">
                    <button
                      onClick={generateText}
                      disabled={loading || !textPrompt.trim()}
                      className="w-full bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Generando...
                        </>
                      ) : (
                        <>
                          <span>🚀</span>
                          Generar Texto
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Describe la imagen que quieres crear:
                  </label>
                  <textarea
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-[#283039] rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                    rows="6"
                    placeholder="Ej: Un paisaje futurista con robots y edificios flotantes al atardecer, estilo cyberpunk..."
                  />
                  <div className="mt-4">
                    <button
                      onClick={generateImage}
                      disabled={loading || !imagePrompt.trim()}
                      className="w-full bg-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Generando...
                        </>
                      ) : (
                        <>
                          <span>🎨</span>
                          Generar Imagen
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Results Section */}
            <div className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {activeTab === 'text' ? '📄 Resultado' : '🖼️ Imagen Generada'}
              </h2>

              {activeTab === 'text' ? (
                <div>
                  {generatedText ? (
                    <div>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
                        <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                          {generatedText}
                        </p>
                      </div>
                      <button
                        onClick={copyText}
                        className="w-full bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <span>📋</span>
                        Copiar Texto
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <div className="text-center">
                        <span className="text-6xl text-gray-300 dark:text-gray-600 mb-4 block">📝</span>
                        <p className="text-gray-500 dark:text-gray-400">
                          El texto generado aparecerá aquí
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {generatedImage ? (
                    <div>
                      <img
                        src={generatedImage}
                        alt="Imagen generada"
                        className="w-full rounded-lg mb-4"
                      />
                      <button
                        onClick={downloadImage}
                        className="w-full bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <span>📥</span>
                        Descargar Imagen
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <div className="text-center">
                        <span className="text-6xl text-gray-300 dark:text-gray-600 mb-4 block">🎨</span>
                        <p className="text-gray-500 dark:text-gray-400">
                          La imagen generada aparecerá aquí
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Info Cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
              <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-2">💡 Tips para Texto</h3>
              <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                <li>• Sé específico con el tema y tono</li>
                <li>• Menciona el público objetivo</li>
                <li>• Indica la longitud deseada</li>
              </ul>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
              <h3 className="font-bold text-purple-900 dark:text-purple-300 mb-2">🎨 Tips para Imágenes</h3>
              <ul className="text-sm text-purple-800 dark:text-purple-300 space-y-1">
                <li>• Describe colores y estilo</li>
                <li>• Menciona la composición</li>
                <li>• Especifica el ambiente/mood</li>
              </ul>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
              <h3 className="font-bold text-green-900 dark:text-green-300 mb-2">⚡ Créditos</h3>
              <ul className="text-sm text-green-800 dark:text-green-300 space-y-1">
                <li>• Texto: 10 créditos (500 palabras)</li>
                <li>• Imagen: 50 créditos (alta calidad)</li>
                <li>• Plan: {user?.plan || 'Gratuito'}</li>
              </ul>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </div>
  );
};

export default Generate;